import * as api from "./client";
import type { paths } from "../types/research-api.generated";
import type { ImportMode, ImportSelection } from "../pages/Research/Cms/utils/bpaImport";

export type OrgRowKind = "primary" | "secondary";

/** One tier a firm holds, attributed to the table it came from. */
export type TableRanking = {
  tableName: string | null;
  tier: number | null;
  rankingType: string | null;
  /** The WordPress ranking_table term id this row's table carries — null pre-sync or when
   * neither upstream service found one. Resolved to the table's research uuid client-side. */
  tableId: number | null;
};

/** One ranking table's own content figures — see `orgSections.ts`'s `tableContentFor`, the only
 * reader of this shape, keyed by table uuid (or the untabled sentinel) on `contentByTable`. */
export type TableContent = {
  reviewStatus: string | null;
  hasSubmission: boolean;
  totalFields: number;
  populatedFields: number;
};

type PublicationConfigSingleResponse =
  paths["/api/v1/publication-configs/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

type ResearchBpaListResponse =
  paths["/api/v1/research-bpas"]["get"]["responses"]["200"]["content"]["application/json"];

type ResearchBpaStatsResponse =
  paths["/api/v1/research-bpas/stats"]["get"]["responses"]["200"]["content"]["application/json"];

type RankingEntityListResponse =
  paths["/api/v1/research-ranking-entities"]["get"]["responses"]["200"]["content"]["application/json"];

type PracticeGroupListResponse =
  paths["/api/v1/research-practice-groups"]["get"]["responses"]["200"]["content"]["application/json"];

export type RankingEntity = RankingEntityListResponse["researchRankingEntities"][number];

export type ResearchBpa = ResearchBpaListResponse["researchBpas"][number];
export type ResearchBpaStat = ResearchBpaStatsResponse["researchBpaStats"][number];

type EditionConfigListResponse =
  paths["/api/v1/edition-configs"]["get"]["responses"]["200"]["content"]["application/json"];

type EditionConfigSingleResponse =
  paths["/api/v1/edition-configs/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

export type EditionConfigUpdateRequest =
  paths["/api/v1/edition-configs/{id}"]["patch"]["requestBody"]["content"]["application/json"];

type EditionConfigUpdateResponse =
  paths["/api/v1/edition-configs/{id}"]["patch"]["responses"]["200"]["content"]["application/json"];

type ResearchProductListResponse =
  paths["/api/v1/research-products"]["get"]["responses"]["200"]["content"]["application/json"];

export type ResearchProductCreateRequest =
  paths["/api/v1/research-products"]["post"]["requestBody"]["content"]["application/json"];

type ResearchProductCreateResponse =
  paths["/api/v1/research-products"]["post"]["responses"]["201"]["content"]["application/json"];

type EditionConfigProductListResponse =
  paths["/api/v1/edition-configs/{id}/products"]["get"]["responses"]["200"]["content"]["application/json"];

export type EditionConfigProductUpdateRequest =
  paths["/api/v1/edition-configs/{id}/products"]["put"]["requestBody"]["content"]["application/json"];

type EditionConfigProductUpdateResponse =
  paths["/api/v1/edition-configs/{id}/products"]["put"]["responses"]["200"]["content"]["application/json"];

export type ResearchProduct = ResearchProductListResponse["products"][number];

type EditionBpaListResponse =
  paths["/api/v1/edition-bpas"]["get"]["responses"]["200"]["content"]["application/json"];

type GeneratedEditionBpaListQuery = paths["/api/v1/edition-bpas"]["get"]["parameters"]["query"];

type EditionBpaListQuery = Omit<GeneratedEditionBpaListQuery, "status"> & {
  status?: GeneratedEditionBpaListQuery["status"] | "archived";
  includeArchived?: boolean;
  mergeTargetEditionBpaId?: string;
};

type ResearchBpaStatsQuery = paths["/api/v1/research-bpas/stats"]["get"]["parameters"]["query"];

type GeneratedEditionBpaUpdateRequest =
  paths["/api/v1/edition-bpas/{id}"]["patch"]["requestBody"]["content"]["application/json"];

export type EditionBpaUpdateRequest = Omit<GeneratedEditionBpaUpdateRequest, "status"> & {
  status?: GeneratedEditionBpaUpdateRequest["status"] | "archived";
};

type EditionBpaUpdateResponse =
  paths["/api/v1/edition-bpas/{id}"]["patch"]["responses"]["200"]["content"]["application/json"];

type EditionBpaSingleResponse =
  paths["/api/v1/edition-bpas/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

export type EditionBpaBulkAssignRequest =
  paths["/api/v1/edition-bpas/bulk-assign"]["post"]["requestBody"]["content"]["application/json"];

type EditionBpaBulkAssignResponse =
  paths["/api/v1/edition-bpas/bulk-assign"]["post"]["responses"]["200"]["content"]["application/json"];

export type EditionConfig = EditionConfigListResponse["editionConfigs"][number];
export type EditionBpaMergeFields = {
  mergeTargetEditionBpaId?: string | null;
  mergeTargetBpaId?: string | null;
  mergeSourceCount?: number;
};
export type EditionBpa = EditionBpaListResponse["editionBpas"][number] &
  EditionBpaMergeFields & {
    outputName?: string | null;
    englishName?: string | null;
  };

type EditorialContentListResponse =
  paths["/api/v1/editorial-content"]["get"]["responses"]["200"]["content"]["application/json"];

type EditorialContentDetailResponse =
  paths["/api/v1/editorial-content/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

/** One company row as the staff-bff composed org-list endpoint returns it. */
type OrgListWireRow = {
  companyId: string;
  companyName: string | null;
  officeName: string | null;
  rowKind: OrgRowKind;
  reviewStatus: string | null;
  hasSubmission: boolean;
  rankingTier: number | null;
  rankingType: string | null;
  /** This edition's ranking — the pair the tier column reads. */
  currentRankingTier: number | null;
  currentRankingType: string | null;
  /** The edition before this one's ranking — the pair the previous-tier column reads. */
  previousRankingTier: number | null;
  previousRankingType: string | null;
  /** Per-table truth behind the scalars above — one entry per firm table the BPA has. */
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  totalFields: number;
  populatedFields: number;
  contentTableIds?: string[];
  contentByTable?: Record<string, TableContent>;
};

type OrganisationsResponse = {
  rows: OrgListWireRow[];
  /** The totalFields denominator for a company holding no editorial content row — what a row
   * WITH content would total at zero clients, contacts and matters. */
  totalFieldsPerEntity: number;
  requestId?: string | null;
};

/** One contact row as the staff-bff composed individual-list endpoint returns it. */
type IndividualListWireRow = {
  contactId: string;
  contactName: string | null;
  companyId: string;
  companyName: string | null;
  rowKind: OrgRowKind;
  reviewStatus: string | null;
  hasSubmission: boolean;
  totalFields: number;
  populatedFields: number;
  currentTier: number | null;
  currentRankingType: string | null;
  previousTier: number | null;
  previousRankingType: string | null;
  /** Per-table truth behind the scalars above — one entry per lawyer table the BPA has. */
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  contentTableIds?: string[];
  contentByTable?: Record<string, TableContent>;
};

type PreviousEditionStatus = "read" | "not_configured" | "unreadable";

type IndividualsResponse = {
  rows: IndividualListWireRow[];
  previousEditionStatus: PreviousEditionStatus;
  /** See `OrganisationsResponse.totalFieldsPerEntity`. */
  totalFieldsPerEntity: number;
  requestId?: string | null;
};

type EditorialContentCreateResponse =
  paths["/api/v1/editorial-content"]["post"]["responses"]["201"]["content"]["application/json"];

type EditorialContentUpdateResponse =
  paths["/api/v1/editorial-content/{id}"]["patch"]["responses"]["200"]["content"]["application/json"];

type EditorialContentBulkResponse =
  paths["/api/v1/editorial-content/bulk"]["patch"]["responses"]["200"]["content"]["application/json"];

type EditorialContentDeleteResponse =
  paths["/api/v1/editorial-content/{id}"]["delete"]["responses"]["200"]["content"]["application/json"];

// `rankingTableScope` is ahead of codegen (research-api.generated.ts hasn't been regenerated
// against a live server since it was added) — added by intersection rather than editing the
// generated file. See ListEditorialContentQuerySchema for why this is distinct from
// rankingTableId: it scopes single-row resolution to the untabled row, never a magic
// rankingTableId value, and is mutually exclusive with it.
export type EditorialContentListQuery = NonNullable<
  paths["/api/v1/editorial-content"]["get"]["parameters"]["query"]
> & { rankingTableScope?: "untabled" };

export type OrganisationsQuery = {
  editionId: string;
  researchBpaId: string;
  /** @deprecated The composed org-list endpoint doesn't sort server-side; kept only so existing callers compile. */
  sort?: string;
  tier?: number;
  rankingType?: string;
  reviewStatus?: string;
  search?: string;
};

export type IndividualsQuery = {
  editionId: string;
  researchBpaId: string;
};

type GeneratedCreateRequest =
  paths["/api/v1/editorial-content"]["post"]["requestBody"]["content"]["application/json"];

export type EditorialContentCreateRequest = Omit<GeneratedCreateRequest, "firmName"> & {
  firmName?: string;
  contactId?: string;
  officeId?: string;
  contactName?: string;
  officeName?: string;
};

export type EditorialContentUpdateRequest =
  paths["/api/v1/editorial-content/{id}"]["patch"]["requestBody"]["content"]["application/json"];

export type EditorialContentBulkRequest =
  paths["/api/v1/editorial-content/bulk"]["patch"]["requestBody"]["content"]["application/json"];

export type EditorialContentRow = EditorialContentListResponse["editorialContent"][number] & {
  contactName?: string | null;
};
export type EditorialContentDetail = EditorialContentDetailResponse["editorialContent"] & {
  contactName?: string | null;
  officeName?: string | null;
};
/**
 * One organisation as `getEditorialContentOrganisations` hands it to the page: the wire row from
 * the composed org-list endpoint, plus the legacy aggregate fields some older callers still read.
 */
export type OrganisationAggregate = {
  companyId: string;
  companyName: string | null;
  officeName?: string | null;
  rowKind?: OrgRowKind;
  reviewStatus: string | null;
  rankingTier?: number | null;
  rankingType?: string | null;
  /** This edition's ranking. Required (not optional) — every caller must account for it. */
  currentRankingTier: number | null;
  currentRankingType: string | null;
  /** The edition before this one's ranking. Required — an omission here is how the previous
   * tier column silently ships empty. */
  previousRankingTier: number | null;
  previousRankingType: string | null;
  /** Per-table truth behind the scalars above — one entry per firm table the BPA has. */
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  /** Whether a submission was parsed, carried through from the wire row untouched. */
  hasSubmission: boolean;
  /** Ranking tables this company holds editorial content for THIS edition — independent of
   * whether it is also ranked in them. Empty for a secondary (carried-over) row. */
  contentTableIds: string[];
  /** This edition's content figures, one entry per table the company holds a row for (plus the
   * untabled sentinel) — see `orgSections.ts`'s `tableContentFor`, its only reader. */
  contentByTable: Record<string, TableContent>;
  fieldCount: number;
  totalFields: number;
  populatedFields: number;
  /**
   * Not carried by the composed org-list endpoint, and nothing renders them. Optional rather
   * than filled with zeros: a count of nought reads as a measurement, and inventing one on a
   * shared type leaves the next reader no way to tell it apart from a real figure.
   */
  approvedCount?: number;
  pendingCount?: number;
  hasUnresolved?: boolean;
};

/**
 * One lawyer as `getEditorialContentIndividuals` hands it to the page: the wire row from the
 * composed individual-list endpoint, `hasSubmission` included and carried through untouched.
 */
export type IndividualAggregate = {
  contactId: string;
  contactName: string | null;
  companyId: string;
  companyName: string | null;
  rowKind: OrgRowKind;
  reviewStatus: string | null;
  hasSubmission: boolean;
  totalFields: number;
  populatedFields: number;
  /** The tier this lawyer holds in the edition being viewed; null when the rankings omit them. */
  currentTier: number | null;
  /** The ranking type that tier came from — tier 0 is named by its category, not shown as 0. */
  currentRankingType: string | null;
  /** The tier this lawyer held the edition before; null when the previous rankings omit them. */
  previousTier: number | null;
  previousRankingType: string | null;
  /** Per-table truth behind the scalars above — one entry per lawyer table the BPA has. */
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  /** Ranking tables this lawyer holds editorial content for THIS edition — independent of
   * whether they are also ranked in them. Empty for a secondary (carried-over) row. */
  contentTableIds: string[];
  /** This edition's content figures, one entry per table the lawyer holds a row for (plus the
   * untabled sentinel) — see `orgSections.ts`'s `tableContentFor`, its only reader. */
  contentByTable: Record<string, TableContent>;
};

type EditionBpaImportPath = paths["/api/v1/edition-bpas/{targetEditionBpaId}/import"]["post"];

export type ImportResult = EditionBpaImportPath["responses"]["200"]["content"]["application/json"];

type ImportRequest = EditionBpaImportPath["requestBody"]["content"]["application/json"];

export type ArchiveResult =
  paths["/api/v1/edition-bpas/{id}/archive"]["patch"]["responses"]["200"]["content"]["application/json"];

type ImportHistoryResponse =
  paths["/api/v1/edition-bpas/{id}/import-history"]["get"]["responses"]["200"]["content"]["application/json"];

export type ImportHistoryEntry = ImportHistoryResponse["importHistory"][number];

export type ContentSummaryResponse =
  paths["/api/v1/edition-bpas/{id}/content-summary"]["get"]["responses"]["200"]["content"]["application/json"];

export type ContentSummaryFirm = ContentSummaryResponse["firms"][number];

type ImportPreviewRequest =
  paths["/api/v1/edition-bpas/{targetEditionBpaId}/import-preview"]["post"]["requestBody"]["content"]["application/json"];

type ImportPreviewResponse =
  paths["/api/v1/edition-bpas/{targetEditionBpaId}/import-preview"]["post"]["responses"]["200"]["content"]["application/json"];

type ImportOverviewResponse =
  paths["/api/v1/edition-bpas/import-overview"]["get"]["responses"]["200"]["content"]["application/json"];

type ScheduleMergeResponse =
  paths["/api/v1/edition-bpas/{targetEditionBpaId}/merge-sources"]["post"]["responses"]["200"]["content"]["application/json"];

type RemoveFromMergeResponse =
  paths["/api/v1/edition-bpas/{id}/merge-target"]["delete"]["responses"]["200"]["content"]["application/json"];

export type FinishSplitResult =
  paths["/api/v1/edition-bpas/{id}/split-finish"]["post"]["responses"]["200"]["content"]["application/json"];

type FinishSplitRequest =
  paths["/api/v1/edition-bpas/{id}/split-finish"]["post"]["requestBody"]["content"]["application/json"];

export type FinishMergeResult =
  paths["/api/v1/edition-bpas/{id}/merge-finish"]["post"]["responses"]["200"]["content"]["application/json"];

type FinishMergeRequest =
  paths["/api/v1/edition-bpas/{id}/merge-finish"]["post"]["requestBody"]["content"]["application/json"];

export type Researcher = {
  id: string;
  name: string;
  email: string;
};

type ResearchersResponse = {
  researchers: Researcher[];
};

type UserNamesResponse = {
  names: Record<string, string>;
};

export type PublicationEdition = {
  id: string;
  editionId: string;
  editionName: string | null;
  status: string;
  submissionOpenDate: string | null;
  submissionCloseDate: string | null;
  publicationFromDate: string | null;
  publicationToDate: string | null;
  bpaCount?: number;
  completedBpas?: number;
  inProgressBpas?: number;
  pendingCount?: number;
  readyForEditingCount?: number;
  readyForTranslationCount?: number;
  readyForReleaseCount?: number;
};

export type PublicationWithEditions = {
  id: string;
  name: string;
  directoryId: string;
  templateType: string;
  inputLanguage: string;
  outputLanguage: string;
  editions: PublicationEdition[];
};

type PublicationsResponse = {
  publications: PublicationWithEditions[];
};

type TemplateFieldsResponse =
  paths["/api/v1/template-configs/{templateType}"]["get"]["responses"]["200"]["content"]["application/json"];

export type TemplateField = TemplateFieldsResponse["templateFields"][number];

type SarsScoresResponse =
  paths["/api/v1/editorial-content/{id}/sars"]["get"]["responses"]["200"]["content"]["application/json"];

export type SarsScoresUpdateRequest =
  paths["/api/v1/editorial-content/{id}/sars"]["put"]["requestBody"]["content"]["application/json"];

type SarsScoresUpdateResponse =
  paths["/api/v1/editorial-content/{id}/sars"]["put"]["responses"]["200"]["content"]["application/json"];

type TranslationsPath = paths["/api/v1/editorial-content/{id}/translations"];
type TranslatePath = paths["/api/v1/editorial-content/{id}/translate"];

export type TranslationsView =
  TranslationsPath["get"]["responses"]["200"]["content"]["application/json"];

export type TranslateResponse =
  TranslatePath["post"]["responses"]["200"]["content"]["application/json"];

export type TranslateBody = NonNullable<
  TranslatePath["post"]["requestBody"]
>["content"]["application/json"];

export type SaveTranslationsBody = NonNullable<
  TranslationsPath["put"]["requestBody"]
>["content"]["application/json"];

export type TranslationTarget = NonNullable<TranslateBody["targets"]>[number];

type EditorialCommentaryResponse =
  paths["/api/v1/edition-bpas/{id}/commentary"]["get"]["responses"]["200"]["content"]["application/json"];

export type EditorialCommentary = EditorialCommentaryResponse["commentary"];

export type EditorialCommentaryUpsertRequest =
  paths["/api/v1/edition-bpas/{id}/commentary"]["put"]["requestBody"]["content"]["application/json"];

export type EditorialCommentaryKey = Pick<
  EditorialCommentaryUpsertRequest,
  "companyId" | "contactId" | "languageIso"
>;

// Not yet in the generated research-api types: this is a staff-bff route, not research-api.
export type RankedLawyer = {
  contactId: string | null;
  rankingTier: number | null;
  rankingType: string | null;
  tableName: string | null;
};

type RankedLawyersResponse = {
  rankedLawyers: RankedLawyer[];
};

// Not yet in the generated research-api types: this is a staff-bff route, not research-api.
export type RankingHistory = {
  /** The tier the lawyer holds in the edition being researched. */
  currentRankingTier: number | null;
  currentRankingType: string | null;
  previousRankingTier: number | null;
  previousRankingType: string | null;
  previousEditionId: string | null;
};

type RankingTableListResponse =
  paths["/api/v1/ranking-tables"]["get"]["responses"]["200"]["content"]["application/json"];

export type RankingTable = RankingTableListResponse["rankingTables"][number];

export type RankingTableFilters = {
  bpaId?: string;
  editionId?: string;
  limit?: number;
  offset?: number;
};

export type BpaFilters = {
  editionId?: string;
  rankingEntityId?: string;
  practiceGroupId?: string;
  directoryId?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

export type EditionFilters = {
  editionId?: string;
  directoryId?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

export type RankingStatus = "draft" | "pending" | "published" | "archived";

export type FirmRankingDetail = {
  id: string;
  rankingTableId: string;
  companyId: string;
  officeId: string | null;
  firmName: string | null;
  officeName: string | null;
  rankingTier: number | null;
  rankingComment: string | null;
  displayOrder: number;
  status: RankingStatus;
  createdAt: string;
  updatedAt: string;
};

export type LawyerRankingDetail = {
  id: string;
  rankingTableId: string;
  contactId: string;
  companyId: string | null;
  officeId: string | null;
  contactName: string | null;
  displayName: string | null;
  firmName: string | null;
  officeName: string | null;
  rankingTier: number | null;
  rankingComment: string | null;
  displayOrder: number;
  status: RankingStatus;
  createdAt: string;
  updatedAt: string;
};

export type CreateFirmRankingRequest = {
  companyId: string;
  officeId?: string;
  firmName?: string;
  officeName?: string;
  rankingTier?: number;
  rankingComment?: string;
  displayOrder?: number;
  status?: RankingStatus;
};

export type UpdateFirmRankingRequest = {
  officeId?: string | null;
  firmName?: string | null;
  officeName?: string | null;
  rankingTier?: number | null;
  rankingComment?: string | null;
  displayOrder?: number;
  status?: RankingStatus;
};

export type CreateLawyerRankingRequest = {
  contactId: string;
  companyId?: string;
  officeId?: string;
  contactName?: string;
  displayName?: string;
  firmName?: string;
  officeName?: string;
  rankingTier?: number;
  rankingComment?: string;
  displayOrder?: number;
  status?: RankingStatus;
};

export type UpdateLawyerRankingRequest = {
  companyId?: string | null;
  officeId?: string | null;
  contactName?: string | null;
  displayName?: string | null;
  firmName?: string | null;
  officeName?: string | null;
  rankingTier?: number | null;
  rankingComment?: string | null;
  displayOrder?: number;
  status?: RankingStatus;
};

type RankingTableSingleResponse = {
  rankingTable: RankingTable;
  requestId: string | null;
};

type FirmRankingsResponse = {
  firmRankings: FirmRankingDetail[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  requestId: string | null;
};

type FirmRankingSingleResponse = {
  firmRanking: FirmRankingDetail;
  requestId: string | null;
};

type LawyerRankingsResponse = {
  lawyerRankings: LawyerRankingDetail[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  requestId: string | null;
};

type LawyerRankingSingleResponse = {
  lawyerRanking: LawyerRankingDetail;
  requestId: string | null;
};

type DeleteRankingResponse = {
  message: string;
  requestId: string | null;
};

function buildQuery(filters: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined) params.append(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const researchCms = {
  async getPublications() {
    return api.get<PublicationsResponse>("/v1/research/publications");
  },

  async getPublication(id: string) {
    return api.get<PublicationConfigSingleResponse>(`/v1/research/publication-configs/${id}`);
  },

  async createPublicationConfig(data: { directoryId: string; templateType: string }) {
    return api.post<PublicationConfigSingleResponse>("/v1/research/publication-configs", data);
  },

  async getEditions(filters?: EditionFilters) {
    const query = buildQuery({ ...filters });
    return api.get<EditionConfigListResponse>(`/v1/research/edition-configs${query}`);
  },

  async getEdition(id: string) {
    return api.get<EditionConfigSingleResponse>(`/v1/research/edition-configs/${id}`);
  },

  async createEditionConfig(data: { editionId: string }) {
    return api.post<EditionConfigSingleResponse>("/v1/research/edition-configs", data);
  },

  async updateEditionConfig(id: string, data: EditionConfigUpdateRequest) {
    return api.patch<EditionConfigUpdateResponse>(`/v1/research/edition-configs/${id}`, data);
  },

  async getResearchProducts() {
    return api.get<ResearchProductListResponse>("/v1/research/research-products");
  },

  async createResearchProduct(data: ResearchProductCreateRequest) {
    return api.post<ResearchProductCreateResponse>("/v1/research/research-products", data);
  },

  async getEditionConfigProducts(id: string) {
    return api.get<EditionConfigProductListResponse>(`/v1/research/edition-configs/${id}/products`);
  },

  async updateEditionConfigProducts(id: string, data: EditionConfigProductUpdateRequest) {
    return api.put<EditionConfigProductUpdateResponse>(
      `/v1/research/edition-configs/${id}/products`,
      data,
    );
  },

  async getEditionBpas(filters: EditionBpaListQuery) {
    const { includeArchived, ...rest } = filters;
    const query = buildQuery({
      ...rest,
      ...(includeArchived ? { includeArchived: "true" } : {}),
    });
    return api.get<EditionBpaListResponse>(`/v1/research/edition-bpas${query}`);
  },

  async getEditionBpa(id: string) {
    return api.get<EditionBpaSingleResponse>(`/v1/research/edition-bpas/${id}`);
  },

  async updateEditionBpa(id: string, data: EditionBpaUpdateRequest) {
    return api.patch<EditionBpaUpdateResponse>(`/v1/research/edition-bpas/${id}`, data);
  },

  async linkEditionBpas(data: { editionId: string; bpaIds: string[] }) {
    return api.post<{ created: number; skipped: number; requestId: string | null }>(
      "/v1/research/edition-bpas",
      data,
    );
  },

  async bulkAssignEditionBpas(data: EditionBpaBulkAssignRequest) {
    return api.post<EditionBpaBulkAssignResponse>("/v1/research/edition-bpas/bulk-assign", data);
  },

  async importEditionBpas(sourceEditionId: string, targetEditionId: string) {
    return api.post<{ created: number; skipped: number; requestId: string }>(
      `/v1/research/edition-bpas/import-from/${sourceEditionId}?targetEditionId=${targetEditionId}`,
    );
  },

  async deleteEditionBpa(id: string) {
    return api.del<{ message: string; requestId: string | null }>(
      `/v1/research/edition-bpas/${id}`,
    );
  },

  async deleteResearchBpa(id: string) {
    return api.del(`/v1/research/bpas/${id}`);
  },

  async deleteAllEditionBpas(editionId: string) {
    return api.del<{ deleted: number; requestId: string }>(
      `/v1/research/edition-bpas/by-edition/${editionId}`,
    );
  },

  async getImportPreview(targetEditionBpaId: string, body: ImportSelection) {
    const request: ImportPreviewRequest = body;
    return api.post<ImportPreviewResponse>(
      `/v1/research/edition-bpas/${targetEditionBpaId}/import-preview`,
      request,
    );
  },

  async importContent(
    targetEditionBpaId: string,
    body: ImportSelection & {
      mode: ImportMode;
      purpose?: "split" | "merge";
      dropMismatchedSars?: boolean;
    },
  ) {
    const request: ImportRequest = body;
    return api.post<ImportResult>(
      `/v1/research/edition-bpas/${targetEditionBpaId}/import`,
      request,
    );
  },

  async scheduleMerge(targetEditionBpaId: string, sourceEditionBpaIds: string[]) {
    return api.post<ScheduleMergeResponse>(
      `/v1/research/edition-bpas/${targetEditionBpaId}/merge-sources`,
      { sourceEditionBpaIds },
    );
  },

  async removeFromMerge(editionBpaId: string) {
    return api.del<RemoveFromMergeResponse>(
      `/v1/research/edition-bpas/${editionBpaId}/merge-target`,
    );
  },

  async archiveEditionBpa(id: string) {
    return api.patch<ArchiveResult>(`/v1/research/edition-bpas/${id}/archive`);
  },

  async finishSplit(originalEditionBpaId: string, newEditionBpaIds: string[]) {
    const request: FinishSplitRequest = { newEditionBpaIds };
    return api.post<FinishSplitResult>(
      `/v1/research/edition-bpas/${originalEditionBpaId}/split-finish`,
      request,
    );
  },

  async finishMerge(targetEditionBpaId: string, sourceEditionBpaIds: string[]) {
    const request: FinishMergeRequest = { sourceEditionBpaIds };
    return api.post<FinishMergeResult>(
      `/v1/research/edition-bpas/${targetEditionBpaId}/merge-finish`,
      request,
    );
  },

  async getResearchers() {
    return api.get<ResearchersResponse>("/v1/research/researchers");
  },

  async getUserNames(ids: string[]) {
    const query = new URLSearchParams({ ids: ids.join(",") });
    return api.get<UserNamesResponse>(`/v1/research/user-names?${query.toString()}`);
  },

  async getBpaById(id: string, editionId?: string) {
    const query = editionId ? `?editionId=${editionId}` : "";
    return api.get<{ researchBpa: Record<string, unknown>; requestId: string | null }>(
      `/v1/research/bpas/${id}${query}`,
    );
  },

  async getBpas(filters?: BpaFilters) {
    const query = buildQuery({ ...filters });
    return api.get<ResearchBpaListResponse>(`/v1/research/bpas${query}`);
  },

  async getBpaStats(filters: ResearchBpaStatsQuery) {
    const query = buildQuery({ ...filters });
    return api.get<ResearchBpaStatsResponse>(`/v1/research/bpas/stats${query}`);
  },

  async createResearchBpa(data: {
    name: string;
    rankingEntityId: string;
    practiceGroupId?: string | null;
    editionId: string;
    taxonomyBpaId?: string | null;
    sourceEditionBpaId?: string | null;
    previousEditionBpaId?: string | null;
    assignedResearcherId?: string | null;
    assignedEditorId?: string | null;
    rankingTables?: Array<{
      name: string;
      subtype: string;
      isTiered?: number;
      submissionType?: string | null;
      refereeType?: string | null;
    }>;
  }) {
    return api.post<{
      researchBpa: Record<string, unknown>;
      editionBpa: { id: string; status: string };
      requestId: string | null;
    }>("/v1/research/bpas", data);
  },

  async updateResearchBpa(
    id: string,
    editionId: string,
    data: {
      name?: string;
      rankingEntityId?: string;
      practiceGroupId?: string | null;
      taxonomyBpaId?: string | null;
      sourceEditionBpaId?: string | null;
      previousEditionBpaId?: string | null;
    },
  ) {
    return api.patch<{ researchBpa: Record<string, unknown>; requestId: string | null }>(
      `/v1/research/bpas/${id}?editionId=${editionId}`,
      data,
    );
  },

  async getRankingEntities(filters?: {
    editionId?: string;
    search?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = buildQuery({ ...filters });
    return api.get<RankingEntityListResponse>(`/v1/research/ranking-entities${query}`);
  },

  async getPracticeGroups(filters: {
    rankingEntityId: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = buildQuery({ ...filters });
    return api.get<PracticeGroupListResponse>(`/v1/research/practice-groups${query}`);
  },

  async createRankingEntity(data: {
    name: string;
    type: string;
    parentId?: string | null;
    taxonomyRegionId?: string | null;
  }) {
    return api.post<{
      researchRankingEntity: { id: string; name: string; type: string };
      requestId: string | null;
    }>("/v1/research/ranking-entities", data);
  },

  async createPracticeGroup(data: { name: string; rankingEntityId: string }) {
    return api.post<{
      researchPracticeGroup: { id: string; name: string };
      requestId: string | null;
    }>("/v1/research/practice-groups", data);
  },

  async getEditorialContentList(filters: EditorialContentListQuery) {
    const query = buildQuery({ ...filters });
    return api.get<EditorialContentListResponse>(`/v1/research/editorial-content${query}`);
  },

  async getEditorialContent(id: string) {
    return api.get<{
      editorialContent: EditorialContentDetail;
      requestId: string;
      templateType: string | null;
    }>(`/v1/research/editorial-content/${id}`);
  },

  async getEditorialContentOrganisations(filters: OrganisationsQuery) {
    const query = buildQuery({ ...filters });
    const response = await api.get<OrganisationsResponse>(
      `/v1/research/editorial-content/organisations${query}`,
    );
    return {
      organisations: response.rows.map(
        (row): OrganisationAggregate => ({
          companyId: row.companyId,
          companyName: row.companyName,
          officeName: row.officeName,
          rowKind: row.rowKind,
          hasSubmission: row.hasSubmission,
          reviewStatus: row.reviewStatus,
          rankingTier: row.rankingTier,
          rankingType: row.rankingType,
          currentRankingTier: row.currentRankingTier,
          currentRankingType: row.currentRankingType,
          previousRankingTier: row.previousRankingTier,
          previousRankingType: row.previousRankingType,
          currentRankings: row.currentRankings,
          previousRankings: row.previousRankings,
          contentTableIds: row.contentTableIds ?? [],
          contentByTable: row.contentByTable ?? {},
          fieldCount: row.populatedFields,
          totalFields: row.totalFields,
          populatedFields: row.populatedFields,
        }),
      ),
      totalFieldsPerEntity: response.totalFieldsPerEntity,
      requestId: response.requestId ?? null,
    };
  },

  async getEditorialContentIndividuals(filters: IndividualsQuery) {
    const query = buildQuery({ ...filters });
    const response = await api.get<IndividualsResponse>(
      `/v1/research/editorial-content/individuals${query}`,
    );
    return {
      individuals: response.rows.map(
        (row): IndividualAggregate => ({
          contactId: row.contactId,
          contactName: row.contactName,
          companyId: row.companyId,
          companyName: row.companyName,
          rowKind: row.rowKind,
          reviewStatus: row.reviewStatus,
          // Carried through as research-api computed it. Deriving it from rowKind made every
          // lawyer holding an editorial_content row read as submitted.
          hasSubmission: row.hasSubmission,
          totalFields: row.totalFields,
          populatedFields: row.populatedFields,
          currentTier: row.currentTier ?? null,
          currentRankingType: row.currentRankingType ?? null,
          previousTier: row.previousTier ?? null,
          previousRankingType: row.previousRankingType ?? null,
          currentRankings: row.currentRankings,
          previousRankings: row.previousRankings,
          contentTableIds: row.contentTableIds ?? [],
          contentByTable: row.contentByTable ?? {},
        }),
      ),
      previousEditionStatus: response.previousEditionStatus,
      totalFieldsPerEntity: response.totalFieldsPerEntity,
      requestId: response.requestId ?? null,
    };
  },

  async createEditorialContent(data: EditorialContentCreateRequest) {
    return api.post<EditorialContentCreateResponse>("/v1/research/editorial-content", data);
  },

  async updateEditorialContent(id: string, data: EditorialContentUpdateRequest) {
    return api.patch<EditorialContentUpdateResponse>(`/v1/research/editorial-content/${id}`, data);
  },

  /** Nothing written yet for this firm/lawyer is a 404 — an answer ("none"), not an error. */
  async getEditorialCommentary(
    editionBpaId: string,
    key: EditorialCommentaryKey,
  ): Promise<EditorialCommentary | null> {
    const query = buildQuery({
      companyId: key.companyId,
      contactId: key.contactId,
      languageIso: key.languageIso,
    });
    try {
      const response = await api.get<EditorialCommentaryResponse>(
        `/v1/research/edition-bpas/${editionBpaId}/commentary${query}`,
      );
      return response.commentary;
    } catch (err) {
      if (err instanceof Error && (err as Error & { status?: number }).status === 404) return null;
      throw err;
    }
  },

  async upsertEditorialCommentary(editionBpaId: string, data: EditorialCommentaryUpsertRequest) {
    return api.put<EditorialCommentaryResponse>(
      `/v1/research/edition-bpas/${editionBpaId}/commentary`,
      data,
    );
  },

  async bulkUpdateEditorialContent(data: EditorialContentBulkRequest) {
    return api.patch<EditorialContentBulkResponse>("/v1/research/editorial-content/bulk", data);
  },

  async deleteEditorialContent(id: string) {
    return api.del<EditorialContentDeleteResponse>(`/v1/research/editorial-content/${id}`);
  },

  async getSars(id: string) {
    return api.get<SarsScoresResponse>(`/v1/research/editorial-content/${id}/sars`);
  },

  async putSars(id: string, body: SarsScoresUpdateRequest) {
    return api.put<SarsScoresUpdateResponse>(`/v1/research/editorial-content/${id}/sars`, body);
  },

  async getTranslations(id: string) {
    return api.get<TranslationsView>(`/v1/research/editorial-content/${id}/translations`);
  },

  async translateContent(id: string, body: TranslateBody = {}) {
    return api.post<TranslateResponse>(`/v1/research/editorial-content/${id}/translate`, body);
  },

  async saveTranslations(id: string, body: SaveTranslationsBody) {
    return api.put<TranslationsView>(`/v1/research/editorial-content/${id}/translations`, body);
  },

  async getTemplateFields(templateType: string) {
    return api.get<TemplateFieldsResponse>(
      `/v1/research/template-configs/${encodeURIComponent(templateType)}`,
    );
  },

  async getRankedLawyers(filters: { companyId: string }) {
    const query = buildQuery({ ...filters });
    return api.get<RankedLawyersResponse>(`/v1/research/ranked-lawyers${query}`);
  },

  async getRankingHistory(filters: {
    editionId: string;
    researchBpaId: string;
    contactId: string;
  }) {
    const query = buildQuery({ ...filters });
    return api.get<RankingHistory>(`/v1/research/ranking-history${query}`);
  },

  async getRankingTables(filters?: RankingTableFilters) {
    const query = buildQuery({ ...filters });
    return api.get<RankingTableListResponse>(`/v1/research/ranking-tables${query}`);
  },

  async getRankingTable(id: string) {
    return api.get<RankingTableSingleResponse>(`/v1/research/ranking-tables/${id}`);
  },

  async createRankingTable(
    editionBpaId: string,
    data: {
      name: string;
      subtype: string;
      submissionType?: string | null;
      refereeType?: string | null;
    },
  ) {
    return api.post<{ rankingTable: RankingTable; requestId: string }>(
      `/v1/research/edition-bpas/${editionBpaId}/ranking-tables`,
      data,
    );
  },

  async updateRankingTables(
    editionBpaId: string,
    tables: Array<{
      id?: string;
      name: string;
      subtype: string;
      submissionType?: string | null;
      refereeType?: string | null;
    }>,
  ) {
    return api.put<{ rankingTables: RankingTable[]; requestId: string }>(
      `/v1/research/edition-bpas/${editionBpaId}/ranking-tables`,
      { rankingTables: tables },
    );
  },

  async updateRankingTable(
    id: string,
    data: {
      name?: string;
      subtype?: string;
      submissionType?: string | null;
      refereeType?: string | null;
    },
  ) {
    return api.put<{ rankingTable: RankingTable; requestId: string }>(
      `/v1/research/ranking-tables/${id}`,
      data,
    );
  },

  async deleteRankingTable(id: string) {
    return api.del<{ requestId: string }>(`/v1/research/ranking-tables/${id}`);
  },

  async getFirmRankings(rankingTableId: string, filters?: { companyId?: string }) {
    const query = buildQuery({ ...filters });
    return api.get<FirmRankingsResponse>(
      `/v1/research/ranking-tables/${rankingTableId}/firm-rankings${query}`,
    );
  },

  async createFirmRanking(rankingTableId: string, data: CreateFirmRankingRequest) {
    return api.post<FirmRankingSingleResponse>(
      `/v1/research/ranking-tables/${rankingTableId}/firm-rankings`,
      data,
    );
  },

  async updateFirmRanking(id: string, data: UpdateFirmRankingRequest) {
    return api.put<FirmRankingSingleResponse>(`/v1/research/firm-rankings/${id}`, data);
  },

  async deleteFirmRanking(id: string) {
    return api.del<DeleteRankingResponse>(`/v1/research/firm-rankings/${id}`);
  },

  async getLawyerRankings(rankingTableId: string, filters?: { contactId?: string }) {
    const query = buildQuery({ ...filters });
    return api.get<LawyerRankingsResponse>(
      `/v1/research/ranking-tables/${rankingTableId}/lawyer-rankings${query}`,
    );
  },

  async createLawyerRanking(rankingTableId: string, data: CreateLawyerRankingRequest) {
    return api.post<LawyerRankingSingleResponse>(
      `/v1/research/ranking-tables/${rankingTableId}/lawyer-rankings`,
      data,
    );
  },

  async updateLawyerRanking(id: string, data: UpdateLawyerRankingRequest) {
    return api.put<LawyerRankingSingleResponse>(`/v1/research/lawyer-rankings/${id}`, data);
  },

  async deleteLawyerRanking(id: string) {
    return api.del<DeleteRankingResponse>(`/v1/research/lawyer-rankings/${id}`);
  },

  async reorderFirmRankings(
    rankingTableId: string,
    items: Array<{ id: string; displayOrder: number }>,
  ) {
    return api.patch<FirmRankingsResponse>(
      `/v1/research/ranking-tables/${rankingTableId}/firm-rankings/reorder`,
      { items },
    );
  },

  async reorderLawyerRankings(
    rankingTableId: string,
    items: Array<{ id: string; displayOrder: number }>,
  ) {
    return api.patch<LawyerRankingsResponse>(
      `/v1/research/ranking-tables/${rankingTableId}/lawyer-rankings/reorder`,
      { items },
    );
  },

  async getImportOverview(editionId: string) {
    return api.get<ImportOverviewResponse>(
      `/v1/research/edition-bpas/import-overview?editionId=${encodeURIComponent(editionId)}`,
    );
  },

  async getImportHistory(editionBpaId: string) {
    return api.get<ImportHistoryResponse>(
      `/v1/research/edition-bpas/${editionBpaId}/import-history`,
    );
  },

  async getContentSummary(editionBpaId: string) {
    return api.get<ContentSummaryResponse>(
      `/v1/research/edition-bpas/${editionBpaId}/content-summary`,
    );
  },
};

export async function fetchAllEditions(filters?: EditionFilters): Promise<EditionConfig[]> {
  const PAGE_SIZE = 100;
  const MAX_PAGES = 10;
  let offset = 0;
  let all: EditionConfig[] = [];

  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await researchCms.getEditions({
      ...filters,
      limit: PAGE_SIZE,
      offset,
    });
    all = [...all, ...result.editionConfigs];
    if (!result.pagination.hasMore) break;
    offset += PAGE_SIZE;
  }

  return all;
}

export async function fetchAllEditionBpas(filters: {
  editionId: string;
  rankingEntityId?: string;
  sourceEditionBpaId?: string;
  sort?: EditionBpaListQuery["sort"];
  includeArchived?: boolean;
}): Promise<EditionBpa[]> {
  const PAGE_SIZE = 200;
  const MAX_PAGES = 10;
  let offset = 0;
  let allBpas: EditionBpa[] = [];

  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await researchCms.getEditionBpas({
      ...filters,
      limit: PAGE_SIZE,
      offset,
    });
    allBpas = [...allBpas, ...result.editionBpas];
    if (!result.pagination.hasMore) break;
    offset += PAGE_SIZE;
  }

  return allBpas;
}
